import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Modal, Button, Form, InputGroup } from "react-bootstrap";
import productHttpService from "../../../../services/http/products/product.service";
import outletStockService from "../../../../services/http/inventory/outlet-stock.service";
import { useParams } from "react-router-dom";
import usePagination, {
  createInitialPagination,
} from "../../../../custom-hooks/usePagination";
import ImageWithBasePath from "../../../../core/img/imagewithbasebath";
import UnlimitLoading from "../../../../components/ui-kit/unlimit-loading";
import UnlimitTable from "../../../../components/ui-kit/unlimit-table";
import { useTranslation } from "react-i18next";
import { Search, CheckSquare, Square } from "react-feather";

const ShopProductsSelectionModal = ({ isOpen, closeModal }) => {
  const [existingProducts, setExistingProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [existingProductsLoading, setExistingProductsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalProducts, setTotalProducts] = useState(0);
  const [hasInitialized, setHasInitialized] = useState(false);

  const { id: outletId } = useParams();
  const { t } = useTranslation();

  const [searchParams, setSearchParams] = useState({
    nameFilter: "",
    lmProductNumberFilter: "",
  });

  // Pagination for selected products
  const initialSelectedPagination = useMemo(
    () => createInitialPagination(t),
    [t]
  );
  const {
    paginationParams: selectedPaginationParams,
    updatePaginationParams: updateSelectedPaginationParams,
  } = usePagination(initialSelectedPagination);

  // Pagination for available products
  const initialAvailablePagination = useMemo(
    () => createInitialPagination(t),
    [t]
  );
  const {
    paginationParams: availablePaginationParams,
    updatePaginationParams: updateAvailablePaginationParams,
  } = usePagination(initialAvailablePagination);

  // Memoize existing product IDs to prevent unnecessary re-renders
  const existingProductIds = useMemo(
    () => existingProducts.map((p) => p.id),
    [existingProducts]
  );

  // Memoize available products (filtered products)
  const availableProducts = useMemo(() => {
    const selectedIds = selectedProducts.map((p) => p.id);
    return allProducts.filter(
      (product) =>
        !existingProductIds.includes(product.id) &&
        !selectedIds.includes(product.id)
    );
  }, [allProducts, existingProductIds, selectedProducts]);

  // Check if all available products are selected
  const isAllAvailableSelected = useMemo(() => {
    if (availableProducts.length === 0) return false;
    return availableProducts.every((product) =>
      selectedProducts.some((selected) => selected.id === product.id)
    );
  }, [availableProducts, selectedProducts]);

  // Check if some (but not all) available products are selected
  const isSomeAvailableSelected = useMemo(() => {
    if (availableProducts.length === 0) return false;
    const selectedAvailableCount = availableProducts.filter((product) =>
      selectedProducts.some((selected) => selected.id === product.id)
    ).length;
    return (
      selectedAvailableCount > 0 &&
      selectedAvailableCount < availableProducts.length
    );
  }, [availableProducts, selectedProducts]);

  // Fetch existing products when modal opens
  const fetchExistingProducts = useCallback(async () => {
    if (!outletId) return;

    const data = {
      page: 1,
      per_page: 1000,
    };

    setExistingProductsLoading(true);
    try {
      const response = await outletStockService.shopProducts(outletId, data);
      if (response.status === 200) {
        const existingData = response.data.data?.data || [];
        setExistingProducts(existingData);

        // Initialize selected products with existing ones
        setSelectedProducts(existingData);
      }
    } catch (error) {
      console.error("Error fetching existing products:", error);
      setError("Failed to load existing products.");
    } finally {
      setExistingProductsLoading(false);
    }
  }, [outletId]);

  // Fetch all products with pagination and search
  const fetchProducts = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const productResponse = await productHttpService.productList({
        page: availablePaginationParams.pagination.current,
        per_page: availablePaginationParams.pagination.pageSize,
        name: searchParams.nameFilter,
        lm_product_number: searchParams.lmProductNumberFilter,
      });

      if (productResponse?.status === 200) {
        const productsList = productResponse.data.data.data || [];
        setTotalProducts(productResponse.data.data.total || 0);
        setAllProducts(productsList);
      }
    } catch (err) {
      console.error("Error fetching products:", err);
      setError("Failed to load products. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, [
    availablePaginationParams.pagination.current,
    availablePaginationParams.pagination.pageSize,
    searchParams.nameFilter,
    searchParams.lmProductNumberFilter,
  ]);

  // Initialize modal data when it opens
  useEffect(() => {
    if (!isOpen) return;

    const initializeModal = async () => {
      if (!hasInitialized) {
        await fetchExistingProducts();
        setHasInitialized(true);
      }
      await fetchProducts();
    };

    initializeModal();
  }, [isOpen, hasInitialized, fetchExistingProducts, fetchProducts]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setAllProducts([]);
      setSelectedProducts([]);
      setError(null);
      setHasInitialized(false);
      setSearchParams({ nameFilter: "", lmProductNumberFilter: "" });

      // Reset pagination
      updateSelectedPaginationParams(initialSelectedPagination);
      updateAvailablePaginationParams(initialAvailablePagination);
    }
  }, [
    isOpen,
    /*   initialSelectedPagination,
    initialAvailablePagination,
    updateSelectedPaginationParams,
    updateAvailablePaginationParams, */
  ]);

  // Move product between available and selected lists
  const toggleProductSelection = useCallback(
    (product) => {
      const isCurrentlySelected = selectedProducts.some(
        (p) => p.id === product.id
      );
      const isExistingProduct = existingProductIds.includes(product.id);

      if (isCurrentlySelected && !isExistingProduct) {
        // Remove from selected (only if not an existing product)
        setSelectedProducts((prev) => prev.filter((p) => p.id !== product.id));
      } else if (!isCurrentlySelected) {
        // Add to selected
        setSelectedProducts((prev) => [...prev, product]);
      }
    },
    [selectedProducts, existingProductIds]
  );

  // Select all available products
  const selectAllAvailableProducts = useCallback(() => {
    const newSelections = availableProducts.filter(
      (product) =>
        !selectedProducts.some((selected) => selected.id === product.id)
    );

    setSelectedProducts((prev) => [...prev, ...newSelections]);
  }, [availableProducts, selectedProducts]);

  // Deselect all available products (keep existing ones)
  const deselectAllAvailableProducts = useCallback(() => {
    setSelectedProducts((prev) =>
      prev.filter((product) => existingProductIds.includes(product.id))
    );
  }, [existingProductIds]);

  // Handle select all toggle
  const handleSelectAllToggle = useCallback(() => {
    if (isAllAvailableSelected) {
      deselectAllAvailableProducts();
    } else {
      selectAllAvailableProducts();
    }
  }, [
    isAllAvailableSelected,
    selectAllAvailableProducts,
    deselectAllAvailableProducts,
  ]);

  // Select all products from current search results
  const selectAllCurrentPageProducts = useCallback(() => {
    const currentPageProducts = availableProducts.filter(
      (product) =>
        !selectedProducts.some((selected) => selected.id === product.id)
    );

    if (currentPageProducts.length > 0) {
      setSelectedProducts((prev) => [...prev, ...currentPageProducts]);
    }
  }, [availableProducts, selectedProducts]);

  // Clear all newly selected products (keep existing ones)
  const clearAllSelections = useCallback(() => {
    setSelectedProducts(existingProducts);
  }, [existingProducts]);

  const handleSearch = useCallback(() => {
    setSearchParams({
      nameFilter: availablePaginationParams.nameFilter || "",
      lmProductNumberFilter:
        availablePaginationParams.lmProductNumberFilter || "",
    });
    // Reset to first page when searching
    updateAvailablePaginationParams({
      ...availablePaginationParams,
      pagination: { ...availablePaginationParams.pagination, current: 1 },
    });
  }, [availablePaginationParams, updateAvailablePaginationParams]);

  const handleSave = async () => {
    // Only save newly selected products (not existing ones)
    const newlySelectedProducts = selectedProducts.filter(
      (product) => !existingProductIds.includes(product.id)
    );

    if (newlySelectedProducts.length === 0) {
      setError("Please select at least one new product");
      return;
    }

    try {
      setLoading(true);
      await saveProductDetails(newlySelectedProducts);
      closeModal();
    } catch (err) {
      setError("Failed to save products. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const saveProductDetails = async (products) => {
    try {
      return await outletStockService.addShopProducts(outletId, {
        products: products.map((product) => ({
          id: product.id,
        })),
      });
    } catch (error) {
      console.error("Error saving product details:", error);
      throw new Error("Failed to save product details");
    }
  };

  const handleSelectedTableChange = useCallback(
    (pagination, filters, sorter) => {
      updateSelectedPaginationParams({
        ...selectedPaginationParams,
        pagination,
      });
    },
    [selectedPaginationParams, updateSelectedPaginationParams]
  );

  const handleAvailableTableChange = useCallback(
    (pagination, filters, sorter) => {
      updateAvailablePaginationParams({
        ...availablePaginationParams,
        pagination,
      });
    },
    [availablePaginationParams, updateAvailablePaginationParams]
  );

  const handleSearchChange = useCallback(
    (e) => {
      updateAvailablePaginationParams({
        ...availablePaginationParams,
        nameFilter: e.target.value,
      });
    },
    [availablePaginationParams, updateAvailablePaginationParams]
  );

  const handleEanChange = useCallback(
    (e) => {
      updateAvailablePaginationParams({
        ...availablePaginationParams,
        lmProductNumberFilter: e.target.value,
      });
    },
    [availablePaginationParams, updateAvailablePaginationParams]
  );

  // Memoize columns to prevent unnecessary re-renders
  const selectedProductsColumns = useMemo(
    () => [
      {
        title: (
          <div className="d-flex align-items-center">
            <Form.Check
              type="checkbox"
              checked={selectedProducts.length > existingProducts.length}
              ref={(input) => {
                if (input) {
                  input.indeterminate =
                    selectedProducts.length > existingProducts.length &&
                    selectedProducts.length <
                      existingProducts.length + availableProducts.length;
                }
              }}
              onChange={() => {
                if (selectedProducts.length > existingProducts.length) {
                  clearAllSelections();
                } else {
                  selectAllCurrentPageProducts();
                }
              }}
              title="Select/Deselect all new products"
            />
          </div>
        ),
        key: "select",
        width: 120,
        render: (text, record) => {
          const isExisting = existingProductIds.includes(record.id);
          return (
            <Form.Check
              type="checkbox"
              checked={true}
              disabled={isExisting}
              onChange={() => toggleProductSelection(record)}
              title={isExisting ? "This product is already in the shop" : ""}
            />
          );
        },
      },
      {
        title: t("tableColumnName.ean"),
        dataIndex: "ean",
        render: (text, record) => record.catalog?.ean || record.ean || "N/A",
      },
      {
        title: t("tableColumnName.lmProductNo"),
        dataIndex: "lm_product_number",
        render: (text, record) =>
          record.lm_product_number ||
          record.catalog?.lm_product_number ||
          "N/A",
      },
      {
        title: t("tableColumnName.productName"),
        dataIndex: "name",
        render: (text, record) => (
          <div className="d-flex align-items-center">
            <ImageWithBasePath
              type="link"
              src={
                record.catalog?.main_product_image || record.main_product_image
              }
              alt="Product Image"
              style={{
                width: 30,
                height: 30,
                marginRight: 10,
                objectFit: "cover",
              }}
            />
            <span>
              {record.catalog?.name || record.name || "Unnamed Product"}
            </span>
          </div>
        ),
      },
    ],
    [
      t,
      toggleProductSelection,
      existingProductIds,
      selectedProducts.length,
      existingProducts.length,
      availableProducts.length,
      clearAllSelections,
      selectAllCurrentPageProducts,
    ]
  );

  const availableProductsColumns = useMemo(
    () => [
      {
        title: (
          <div className="d-flex align-items-center">
            <Form.Check
              type="checkbox"
              checked={isAllAvailableSelected}
              ref={(input) => {
                if (input) {
                  input.indeterminate = isSomeAvailableSelected;
                }
              }}
              onChange={handleSelectAllToggle}
              title="Select/Deselect all products on this page"
            />
          </div>
        ),
        key: "select",
        width: 120,
        render: (text, record) => (
          <Form.Check
            type="checkbox"
            checked={selectedProducts.some((p) => p.id === record.id)}
            onChange={() => toggleProductSelection(record)}
          />
        ),
      },
      {
        title: t("tableColumnName.ean"),
        dataIndex: "ean",
        render: (text, record) => record.catalog?.ean || record.ean || "N/A",
      },
      {
        title: t("tableColumnName.lmProductNo"),
        dataIndex: "lm_product_number",
        render: (text, record) =>
          record.lm_product_number ||
          record.catalog?.lm_product_number ||
          "N/A",
      },
      {
        title: t("tableColumnName.productName"),
        dataIndex: "name",
        render: (text, record) => (
          <div className="d-flex align-items-center">
            <ImageWithBasePath
              type="link"
              src={
                record.catalog?.main_product_image || record.main_product_image
              }
              alt="Product Image"
              style={{
                width: 30,
                height: 30,
                marginRight: 10,
                objectFit: "cover",
              }}
            />
            <span>
              {record.catalog?.name || record.name || "Unnamed Product"}
            </span>
          </div>
        ),
      },
    ],
    [
      t,
      toggleProductSelection,
      isAllAvailableSelected,
      isSomeAvailableSelected,
      handleSelectAllToggle,
      selectedProducts,
    ]
  );

  // Calculate newly selected products count
  const newlySelectedCount = useMemo(() => {
    return selectedProducts.filter(
      (product) => !existingProductIds.includes(product.id)
    ).length;
  }, [selectedProducts, existingProductIds]);

  if (!isOpen) return null;

  return (
    <Modal show={isOpen} onHide={closeModal} centered size="xl">
      <Modal.Header closeButton>
        <Modal.Title>{t("shop.addProductToShop")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        {/* Selected Products Section */}
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div className="fw-bold">
              {t("shop.shopProductList")} ({selectedProducts.length})
              {existingProducts.length > 0 && (
                <small className="text-muted ms-2">
                  ({existingProducts.length} existing, {newlySelectedCount} new)
                </small>
              )}
            </div>

            {newlySelectedCount > 0 && (
              <div className="d-flex gap-2">
                <button
                  className="btn btn-primary"
                  onClick={clearAllSelections}
                >
                  {t("general.button.clearSelection")}
                </button>
              </div>
            )}
          </div>

          {existingProductsLoading ? (
            <div className="d-flex justify-content-center py-3">
              <UnlimitLoading width={30} height={30} />
            </div>
          ) : selectedProducts.length === 0 ? (
            <div className="text-muted p-3 border rounded">
              {t("shop.noProductsSelected")}
            </div>
          ) : (
            <div style={{ maxHeight: "300px", overflowY: "auto" }}>
              <UnlimitTable
                columns={selectedProductsColumns}
                dataSource={selectedProducts}
                rowKey={(record) => record.id}
                pagination={false}
                size="small"
              />
            </div>
          )}
        </div>

        <hr />

        {/* Search and Product List Section */}
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div className="fw-bold">{t("shop.lmProductList")}</div>

          {availableProducts.length > 0 && (
            <div className="d-flex gap-2">
              <button
                onClick={selectAllCurrentPageProducts}
                disabled={isAllAvailableSelected}
                className="btn btn-added"
              >
                {t("general.button.selectAllPage")}
              </button>
            </div>
          )}
        </div>

        <InputGroup className="mb-3">
          <Form.Control
            placeholder="LM Product No"
            value={availablePaginationParams.lmProductNumberFilter || ""}
            onChange={handleEanChange}
          />
          <Form.Control
            placeholder={t("tableColumnName.productName")}
            value={availablePaginationParams.nameFilter || ""}
            onChange={handleSearchChange}
          />
          <Button variant="danger" onClick={handleSearch} disabled={loading}>
            <Search size={16} />
          </Button>
        </InputGroup>

        {loading ? (
          <div className="d-flex justify-content-center py-4">
            <UnlimitLoading width={50} height={50} />
          </div>
        ) : (
          <div style={{ maxHeight: "400px", overflowY: "auto" }}>
            <UnlimitTable
              columns={availableProductsColumns}
              dataSource={availableProducts}
              rowKey={(record) => record.id}
              pagination={{
                ...availablePaginationParams.pagination,
                total: totalProducts,
                pageSizeOptions: [10, 20, 50, 100, 200, 500, 1000],
              }}
              onChange={handleAvailableTableChange}
              size="small"
              loading={loading}
            />
          </div>
        )}

        {/* Selection Summary */}
        {(availableProducts.length > 0 || newlySelectedCount > 0) && (
          <div className="mt-3 p-2 bg-light rounded">
            <div className="d-flex justify-content-between align-items-center text-sm">
              <span>
                {t("general.availableProducts")}: {availableProducts.length} |
                {t("general.selectedForAddition")}: {newlySelectedCount}
              </span>
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={closeModal}
          style={{ fontSize: "0.875rem", padding: "0.375rem 0.75rem" }}
        >
          {t("general.button.cancel")}
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={newlySelectedCount === 0 || loading}
          style={{ fontSize: "0.875rem", padding: "0.375rem 0.75rem" }}
        >
          {loading ? (
            <>
              <UnlimitLoading width={14} height={14} className="me-1" />
              {t("general.button.adding")}...
            </>
          ) : (
            <>
              {t("general.button.add")} ({newlySelectedCount})
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ShopProductsSelectionModal;
